import React from 'react';
import Modal from 'react-bootstrap/Modal';
import { strings } from '../../resources/strings';
import { CustomerOriginType } from '../../enums/CustomerOriginType';
import { useAuth } from '../../contexts/AuthContext';
import ServicesContext from '../../contexts/ServicesContext';
import { SaveCustomerAddressInput, SaveCustomerInput } from '../../services/Customers/SaveCustomer/SaveCustomerInput';
import { NewCustomerInput } from '../../services/Customers/NewCustomer/NewCustomerInput';
import { UpdateCustomerInput } from '../../services/Customers/UpdateCustomer/UpdateCustomerInput';
import { Button } from '../../common-components/Button/Button';
import { TabMenu } from '../../common-components/TabMenu/TabMenu';
import { If } from '../../common-components/If/If';
import { WaitingIcon } from '../../common-components/WaitingIcon/WaitingIcon';
import { TabItem } from '../../common-components/TabMenu/styles';
import { CustomerBasicDataType, TabBasicData } from './TabBasicData/TabBasicData';
import { CustomerAddressType, TabAddress } from './TabAddress/TabAddress';
import { FooterContent } from './styles';

const TabOptions = {
    BasicData: 'tab-basic-data',
    Address: 'tab-address'
}

interface IModalCustomerRegistrationProps {
    close: (changed?: boolean, customerId?: number) => void;
    editCustomerId?: number;
}

const ModalCustomerRegistration: React.FC<IModalCustomerRegistrationProps> = ({
    close,
    editCustomerId
}) => {

    const { session } = useAuth();
    const [ activeTab, setActiveTab ] = React.useState<string>(TabOptions.BasicData);
    const [ loading, setLoading ] = React.useState<boolean>(!!editCustomerId && editCustomerId > 0);
    const [ basicData, setBasicData ] = React.useState<CustomerBasicDataType|null|undefined>(null);
    const [ address, setAddress ] = React.useState<CustomerAddressType|null|undefined>(null);

    const {
        toastService,
        alertService,
        newCustomerService,
        getCustomerByIdService,
        updateCustomerService
    } = React.useContext(ServicesContext);

    React.useEffect(() => {
        getEditCustomer();
    }, [editCustomerId]);

    const memoBody = React.useMemo(() => {
        if (loading) {
            return (
                <WaitingIcon />
            );
        }

        return (
            <>
                <TabMenu
                    tabs={[
                        {
                            key: TabOptions.BasicData,
                            text: strings.BasicData
                        },
                        {
                            key: TabOptions.Address,
                            text: strings.Address
                        }
                    ]}
                    activeKey={activeTab}
                    change={(key) => setActiveTab(key)}
                />
                <TabItem>
                    <If condition={activeTab === TabOptions.BasicData}>
                        <TabBasicData basicData={basicData} setBasicData={setBasicData} editCustomerId={editCustomerId} />
                    </If>
                    <If condition={activeTab === TabOptions.Address}>
                        <TabAddress address={address} setAddress={setAddress} />
                    </If>
                </TabItem>
            </>
        );
    }, [ loading, activeTab, basicData, setBasicData, address, setAddress, editCustomerId ]);

    const memoFooter = React.useMemo(() => {
        if (loading) return null;

        return (
            <>
                <Button text={strings.Cancel} width='150px' marginRight='20px' click={close} />
                <Button text={strings.Save} width='150px' click={() => save()} />
            </>
        );
    }, [ loading, activeTab, basicData, setBasicData, address, setAddress, editCustomerId ]);

    return (
        <Modal
            size="lg"
            show={true}
            onHide={close}
            backdrop="static"
            keyboard={false}
        >
            <Modal.Header closeButton style={{height:'50px'}}>
                <Modal.Title>
                    Cadastro de Cliente
                </Modal.Title>
            </Modal.Header>

            <Modal.Body style={{maxHeight:'78vh',overflowY:'auto'}}>
                {memoBody}
            </Modal.Body>

            <Modal.Footer>
                <FooterContent>
                    {memoFooter}
                </FooterContent>
            </Modal.Footer>
        </Modal>
    );

    function save() {
        if (editCustomerId && editCustomerId > 0) {
            edit();
            return;
        }

        insert();
    }

    function edit() {
        const input = createSaveCustomerInput() as UpdateCustomerInput;

        input.id = editCustomerId;

        if (!customerInputIsValid(input)) {
            return;
        }

        updateCustomerService.handle(input)
            .then(result => {
                if (result?.value?.result) {
                    toastService.success(strings.CustomerSuccessfullyUpdated);
                    close(true, editCustomerId);
                    return;
                }
                if (result?.value?.errorMessage) {
                    alertService.warning(result?.value?.errorMessage);
                } else {
                    toastService.warning(strings.OpsSomethingWrongSaving);
                }
            })
            .catch(() => {
                toastService.warning(strings.OpsSomethingWrongSendTheData);
            });
    }

    function insert() {
        const input = createSaveCustomerInput() as NewCustomerInput;

        if (!customerInputIsValid(input)) {
            return;
        }

        input.customerOrigin = CustomerOriginType.Sislog;

        newCustomerService.handle(input)
            .then(result => {
                if (result?.value?.result) {
                    toastService.success(strings.CustomerSuccessfullyRegistered);
                    close(true);
                    return;
                }
                if (result?.value?.errorMessage) {
                    alertService.warning(result?.value?.errorMessage);
                } else {
                    toastService.warning(strings.OpsSomethingWrongSaving);
                }
            })
            .catch(() => {
                toastService.warning(strings.OpsSomethingWrongSendTheData);
            });
    }

    function createSaveCustomerInput() {
        const input = new SaveCustomerInput();
        input.firstName = basicData?.firstName;
        input.lastName = basicData?.lastName;
        input.phone = basicData?.phone;
        input.document = basicData?.documentNumber;
        input.email = basicData?.email;
        if (address?.zip || address?.address || address?.city || address?.provinceCode || address?.neighborhood) {
            input.address = new SaveCustomerAddressInput();
            input.address.address1 = address?.address;
            input.address.address2 = address?.additionalAddress;
            input.address.addressNumber = address && address.addressNumber ? +address.addressNumber : undefined;
            input.address.city = address?.city;
            input.address.neighborhood = address?.neighborhood;
            input.address.provinceCode = address?.provinceCode;
            input.address.zip = address?.zip;
            input.address.country = strings.Brazil;
        }
        return input;
    }

    function customerInputIsValid(input: { firstName?: string, lastName?: string, email?: string }) {
        if (!input.firstName) {
            toastService.warning(strings.InformTheCustomerName);
            return false;
        }

        if (!input.lastName) {
            toastService.warning(strings.InformTheCustomerLastName);
            return false;
        }

        if (!input.email) {
            toastService.warning(strings.InformTheCustomerEmail);
            return false;
        }

        return !!input;
    }

    function getEditCustomer() {
        if (!editCustomerId) return;
        getCustomerByIdService.handle({id: editCustomerId})
            .then(result => {
                const customer = result?.value?.customer;
                
                if (!customer) {
                    return;
                }
                
                const customerBasicData = {} as CustomerBasicDataType;
                customerBasicData.firstName = customer.firstName;
                customerBasicData.lastName = customer.lastName;
                customerBasicData.phone = customer.phone;
                customerBasicData.documentNumber = customer.document;
                customerBasicData.email = customer.email;

                const firstCustomerAddress = customer.customerAddresses && customer.customerAddresses.length > 0
                        ? customer.customerAddresses[0]
                        : null;

                if (firstCustomerAddress) {
                    const customerAddress = {} as CustomerAddressType;
                    customerAddress.zip = firstCustomerAddress.zip;
                    customerAddress.address = firstCustomerAddress.address;
                    customerAddress.addressNumber = firstCustomerAddress.addressNumber ? `${firstCustomerAddress.addressNumber}` : undefined;
                    customerAddress.neighborhood = firstCustomerAddress.neighborhood;
                    customerAddress.additionalAddress = firstCustomerAddress.additionalAddress;
                    customerAddress.city = firstCustomerAddress.city;
                    customerAddress.provinceCode = firstCustomerAddress.provinceCode;

                    setAddress(customerAddress);
                }
                
                setBasicData(customerBasicData);
                setLoading(false);
            });
    }
}

export {ModalCustomerRegistration};